<script>
import { useGlobalStore } from "@/stores/globalStore";

export default {
  setup() {
    const globalStore = useGlobalStore();
    return { globalStore };
  },
  props: {
    guideKey: { type: String, required: true },
  },
  data() {
    return {
      currentStep: 1,
      totalSteps: 0,
      guideData: {},
      imageError: false,
    };
  },
  computed: {
    stepKey() {
      return `step${this.currentStep}`;
    },
  },
  watch: {
    guideKey: {
      immediate: true,
      handler(newKey) {
        this.loadGuideData(newKey);
      },
    },
    currentStep() {
      this.loadGuideData(this.guideKey);
    },
  },
  methods: {
    loadGuideData(guideKey) {
      const keyBase = `guide.${guideKey}.${this.stepKey}`;

      this.guideData = {
        title: `${keyBase}.title`,
        text: this.$te(`${keyBase}.text`) ? `${keyBase}.text` : "",
        step: this.$t(`${keyBase}.step`),
        important: this.$te(`${keyBase}.important`) ? this.$t(`${keyBase}.important`) : null,
        important2: this.$te(`${keyBase}.important2`) ? this.$t(`${keyBase}.important2`) : null,
        important3: this.$te(`${keyBase}.important3`) ? this.$t(`${keyBase}.important3`) : null,
        center: this.$te(`${keyBase}.center`) ? this.$t(`${keyBase}.center`) : null,
      };

      const guide = this.$i18n.messages[this.$i18n.locale]?.guide?.[guideKey];
      this.totalSteps = guide ? Object.keys(guide).length : 0;
    },
    nextStep() {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      } else {
        this.globalStore.showModalGuide = false;
        this.currentStep = 1;
        this.totalSteps = 0;
        this.guideData = {};
      }
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep--;
    },
  },
};
</script>

<template>
  <div class="guide-content">
    <StepSwitchItem :currentStep="currentStep" :steps="totalSteps" />

    <div class="guide-title">
      <i18n-t :keypath="guideData.title" tag="span">
        <template v-slot:step>
          <span :style="{ color: 'var(--factstorylight)' }">{{
            guideData.step
          }}</span>
        </template>
      </i18n-t>
    </div>

    <img
      alt="guide"
      loading="lazy"
      class="guide-image"
      @error="imageError = true"
      v-if="!imageError"
      :src="`/images/${guideKey}-${stepKey}.png`"
    />

    <div class="guide-explication" v-if="guideData.text">
      <i18n-t :keypath="guideData.text" tag="span">
        <template v-if="guideData.important" v-slot:important>
          <span :style="{ fontWeight: 900 }">
            {{ guideData.important }}
          </span>
        </template>
        <template v-if="guideData.important2" v-slot:important2>
          <span :style="{ fontWeight: 900 }">
            {{ guideData.important2 }}
          </span>
        </template>
        <template v-if="guideData.important3" v-slot:important3>
          <span :style="{ fontWeight: 900 }">
            {{ guideData.important3 }}
          </span>
        </template>
        <template v-if="guideData.center" v-slot:center>
          <div class="guide-title-warning">
            {{ guideData.center }}
          </div>
        </template>
      </i18n-t>
    </div>

    <div class="guide-buttons">
      <ButtonItem
        v-if="currentStep > 1"
        :msg="$t('guide.return')"
        class="guide-button return-button"
        @click="prevStep"
      />
      <ButtonItem
        :msg="currentStep < totalSteps ? $t('guide.next') : $t('guide.confirm')"
        class="guide-button"
        @click="nextStep"
      />
    </div>
  </div>
</template>

<style scoped>
.guide-content {
  height: 100%;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.guide-title {
  text-align: center;
  font-size: 1.6rem;
  font-weight: 700;
  padding: 40px 0;
}
.guide-title-warning {
  text-align: center;
  font-size: 1.6rem;
  font-weight: 300;
  padding: 0 0 40px;
}
.guide-image {
  height: 380px;
  margin: 0 auto 40px;
  object-fit: contain;
}
.guide-explication {
  width: 560px;
  color: var(--text-medium-emphasis);
  margin: 0 auto auto;
  line-height: normal;
}
.guide-buttons {
  width: 560px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  gap: 20px;
}
.guide-button {
  font-size: 1rem;
  font-weight: 500;
  width: 40%;
  height: 40px;
  color: var(--text);
}
.return-button {
  border-radius: 10px;
  background-color: var(--bg3);
}
.return-button:hover:not(:disabled) {
  background-color: var(--bg2);
}
</style>
